import React, { useState, useEffect } from "react";
import { Avatar } from "../shared/Avatar";
import { CustomIcon } from "../shared/CustomIcon";
import { fullName, formatDate, uid } from "../../lib/format";
import { apiCreateEvent, apiUpdateEvent, apiDeleteEvent } from "../../lib/calendarApi";
import AuthorityAssignmentField from "../shared/AuthorityAssignmentField";
import { CoverImage } from "../shared/CoverImage";
import { TextStyleControls } from "../shared/TextStyleControls";
import { ColorPickerField } from "../services/ServiceForm";

function EventsPage({ currentUser, isSuperAdmin, isLimitedAdmin, events, setEvents, members, users, addAudit, addNotification, persist }) {
  const [showModal, setShowModal] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [viewEvent, setViewEvent] = useState(null);
  const [showQR, setShowQR] = useState(null);
  const [prefillDate, setPrefillDate] = useState("");

  const canManage = !!(isSuperAdmin || isLimitedAdmin);
  const canEditRecord = (ev) => canManage || (Array.isArray(ev?.authorityIds) && ev.authorityIds.includes(currentUser?.id));

  // Click-to-add from the Calendar → /events?new=1&date=YYYY-MM-DD opens the form pre-filled.
  useEffect(() => {
    const url = new URL(window.location.href);
    const isNew = url.searchParams.get("new") === "1";
    const d = url.searchParams.get("date") || "";
    if (isNew && canManage) {
      setPrefillDate(/^\d{4}-\d{2}-\d{2}$/.test(d) ? d : "");
      setEditEvent(null);
      setShowModal(true);
      // Clean the URL once we've consumed the prefill — keeps subsequent reopens clean.
      url.searchParams.delete("new");
      url.searchParams.delete("date");
      window.history.replaceState({}, "", `${url.pathname}${url.search ? `?${url.searchParams.toString()}` : ""}`);
    }
  }, []);

  const saveEvent = async (data) => {
    try {
      if (data.id) {
        const fresh = await apiUpdateEvent(data.id, data);
        setEvents(prev => prev.map(e => e.id === fresh.id ? fresh : e));
      } else {
        const fresh = await apiCreateEvent(data);
        setEvents(prev => [...prev, fresh]);
        addAudit(currentUser.id, currentUser.name, "Created event", `Added ${fresh.name}`);
      }
    } catch (e) {
      alert(e.message || "Could not save event");
      return;
    }
    setShowModal(false); setEditEvent(null); setPrefillDate("");
  };

  const deleteEvent = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await apiDeleteEvent(id);
      setEvents(prev => prev.filter(e => e.id !== id));
    } catch (e) {
      alert(e.message || "Could not delete event");
    }
  };

  const registerMember = async (eventId, memberId) => {
    const ev = events.find(e => e.id === eventId);
    if (!ev) return;
    const regs = ev.registrations || [];
    const newRegs = regs.includes(memberId) ? regs.filter(r => r !== memberId) : [...regs, memberId];
    try {
      const fresh = await apiUpdateEvent(eventId, { ...ev, registrations: newRegs });
      setEvents(prev => prev.map(e => e.id === fresh.id ? fresh : e));
    } catch (e) {
      alert(e.message || "Could not update registrations");
    }
  };

  if(viewEvent) {
    const ev = events.find(e=>e.id===viewEvent);
    if(!ev){setViewEvent(null);return null;}
    const canEdit = canEditRecord(ev);
    const registeredMembers = members.filter(m=>(ev.registrations||[]).includes(m.id));
    const notRegistered = members.filter(m=>!(ev.registrations||[]).includes(m.id));
    return (
      <div>
        <div className="flex items-center gap-8 mb-20">
          <button className="btn btn-outline" onClick={()=>setViewEvent(null)}>← Back</button>
          {canEdit&&<button className="btn btn-primary" onClick={()=>{setEditEvent(ev);setShowModal(true);}}>Edit</button>}
          {!canEdit && (
            <span data-testid={`event-readonly-${ev.id}`} style={{ fontSize: 10, color: "var(--kc-sub)", letterSpacing: "0.06em", textTransform: "uppercase", padding: "4px 10px", borderRadius: 999, background: "color-mix(in srgb, var(--kc-accent) 10%, transparent)", border: "1px solid color-mix(in srgb, var(--kc-accent) 25%, transparent)", display: "inline-flex", alignItems: "center", gap: 4 }}><CustomIcon name="lock" size={11} /> View only</span>
          )}
        </div>
        <div className="profile-hero" style={{marginBottom:20}}>
          <div style={{fontSize:48, display: "inline-flex"}}><CustomIcon name="event-ticket" size={48} /></div>
          <div>
            <div className="profile-name">{ev.name}</div>
            <div className="profile-meta">{formatDate(ev.date)} · {(ev.registrations||[]).length} / {ev.capacity||"∞"} registered</div>
            {ev.recurrence && ev.recurrence.type && ev.recurrence.type !== "none" && (
              <div style={{ marginTop: 4, fontSize: 11, color: "var(--gold)", letterSpacing: "0.05em", textTransform: "uppercase" }}>↻ {ev.recurrence.type.replace("-", " ")}</div>
            )}
          </div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
          <div className="card" style={{padding:18}}>
            <div className="form-section-title mb-16">Registered ({registeredMembers.length})</div>
            {registeredMembers.length===0?<div style={{fontSize:13,color:"var(--text3)"}}>No registrations yet</div>:
              registeredMembers.map(m=>(
                <div key={m.id} className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-8"><Avatar name={fullName(m)} photo={m.photo} memberId={m.id} status={m.memberStatus} style={{width:26,height:26,fontSize:10}}/><span style={{fontSize:13}}>{fullName(m)}</span></div>
                  {canEdit&&<button className="btn btn-sm btn-danger" onClick={()=>registerMember(ev.id,m.id)}>Remove</button>}
                </div>
              ))
            }
          </div>
          {canEdit&&(
            <div className="card" style={{padding:18}}>
              <div className="form-section-title mb-16">Add Registrations</div>
              {notRegistered.map(m=>(
                <div key={m.id} className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-8"><Avatar name={fullName(m)} photo={m.photo} memberId={m.id} status={m.memberStatus} style={{width:26,height:26,fontSize:10}}/><span style={{fontSize:13}}>{fullName(m)}</span></div>
                  <button className="btn btn-sm btn-gold" onClick={()=>registerMember(ev.id,m.id)}>+ Add</button>
                </div>
              ))}
            </div>
          )}
        </div>
        {showModal&&<EventModal event={editEvent} users={users} canManageAuthority={canManage} canEdit={editEvent ? canEditRecord(editEvent) : canManage} isSuperAdmin={!!isSuperAdmin} prefillDate={prefillDate} onSave={saveEvent} onClose={()=>{setShowModal(false);setEditEvent(null);setPrefillDate("");}}/>}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-20">
        <div style={{fontSize:13,color:"var(--text3)"}}>{events.length} events</div>
        {canManage&&<button className="btn btn-gold" data-testid="events-add-btn" onClick={()=>{setEditEvent(null);setShowModal(true);}}>+ Create Event</button>}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(270px,1fr))",gap:14}}>
        {events.map(ev=>{
          const can = canEditRecord(ev);
          const rec = ev.recurrence && ev.recurrence.type && ev.recurrence.type !== "none" ? ev.recurrence.type : null;
          // Iter 81: semantic border — cancelled → red, tentative → amber,
          // otherwise blue (confirmed / scheduled).
          const st = String(ev.status || "").toLowerCase();
          const evVariant = /cancel/.test(st) ? "red" : /tentative|pending|draft/.test(st) ? "amber" : "blue";
          return (
          <div
            key={ev.id}
            className={`dept-card card-${evVariant}`}
            data-testid={`events-card-${ev.id}`}
          >
            <CoverImage
              value={ev.coverImage || ""}
              title={ev.name}
              subtitle={`${formatDate(ev.date)}${rec ? ` · ↻ ${rec.replace("-"," ")}` : ""}`}
              iconName="event-ticket"
              overlay={typeof ev.overlay === "number" ? ev.overlay : 65}
              textColor={ev.textColor || "white"}
              textEffect={ev.textEffect || "shadow"}
              data-testid={`events-cover-${ev.id}`}
              style={{ borderRadius: 0, borderLeft: "none", borderRight: "none", borderTop: "none", height: 160, aspectRatio: "auto" }}
            />
            <div className="listing-card-body">
              <div className="flex items-center justify-between">
                <span className="text-meta">{formatDate(ev.date)}</span>
                <span className="text-meta">{(ev.registrations||[]).length} registered</span>
              </div>
              {ev.description && <div className="listing-card-desc">{ev.description}</div>}
              {!can && (
                <div data-testid={`events-readonly-${ev.id}`} className="text-meta" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <CustomIcon name="lock" size={11} /> View only
                </div>
              )}
              <div className="flex flex-wrap" style={{ gap: 6, marginTop: 4 }}>
                <button className="btn-secondary btn-sm" onClick={()=>setViewEvent(ev.id)}>View</button>
                {can && (
                  <button
                    type="button"
                    data-testid={`events-delete-${ev.id}`}
                    onClick={()=>deleteEvent(ev.id)}
                    style={{
                      background: "transparent", border: "none",
                      color: "var(--kc-red)", cursor: "pointer",
                      fontFamily: "'Inter',sans-serif", fontSize: 11,
                      fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
                      padding: "6px 10px",
                    }}
                  >Delete</button>
                )}
              </div>
            </div>
          </div>
          );
        })}
      </div>
      {showQR&&(
        <div className="modal-overlay" onClick={()=>setShowQR(null)}>
          <div className="modal" style={{maxWidth:360}}>
            <div className="modal-header"><div className="modal-title">Event QR Code</div><button className="modal-close" onClick={()=>setShowQR(null)}>×</button></div>
            <div className="modal-body">
              <div className="qr-box">
                <div className="qr-placeholder"><CustomIcon name="qr" size={56} /></div>
                <div style={{fontFamily:"'Cinzel',serif",fontSize:14,color:"var(--gold)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:6}}>{showQR.name}</div>
                <div style={{fontSize:12,color:"var(--kc-sub)"}}>{formatDate(showQR.date)}</div>
                <div style={{marginTop:12,padding:"6px 12px",background:"color-mix(in srgb, var(--kc-accent) 15%, transparent)",borderRadius:6,fontSize:11,color:"var(--gold)",fontFamily:"monospace"}}>kgc-events/{showQR.id}</div>
              </div>
              <p style={{fontSize:12,color:"var(--text3)",textAlign:"center",marginTop:12,fontFamily:"Montserrat,sans-serif"}}>In production this links to a public registration page.</p>
            </div>
          </div>
        </div>
      )}
      {showModal&&<EventModal event={editEvent} users={users} canManageAuthority={canManage} canEdit={editEvent ? canEditRecord(editEvent) : canManage} isSuperAdmin={!!isSuperAdmin} prefillDate={prefillDate} onSave={saveEvent} onClose={()=>{setShowModal(false);setEditEvent(null);setPrefillDate("");}}/>}
    </div>
  );
}

const DOW_NAMES = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

function EventModal({ event, users, canManageAuthority, canEdit, isSuperAdmin = false, prefillDate, onSave, onClose }) {
  const initial = event || {
    name: "",
    date: prefillDate || "",
    description: "",
    capacity: "",
    time: "",
    color: "",
    authorityIds: [],
    recurrence: { type: "none", daysOfWeek: [], interval: 1, end: "never", until: "", count: "" },
  };
  // Ensure recurrence shape on existing events that pre-date the feature.
  if (!initial.recurrence) initial.recurrence = { type: "none", daysOfWeek: [], interval: 1, end: "never", until: "", count: "" };
  const [form, setForm] = useState(initial);
  // "Edit this only vs all future" ask — only relevant on save of recurring edits.
  const [editScopeAsk, setEditScopeAsk] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setRec = (k, v) => setForm(f => ({ ...f, recurrence: { ...(f.recurrence || {}), [k]: v } }));
  const toggleDow = (idx) => {
    const cur = Array.isArray(form.recurrence?.daysOfWeek) ? form.recurrence.daysOfWeek : [];
    const next = cur.includes(idx) ? cur.filter(x => x !== idx) : [...cur, idx].sort();
    setRec("daysOfWeek", next);
  };
  const readonly = !canEdit;
  const isRecurringEdit = event && event.recurrence && event.recurrence.type && event.recurrence.type !== "none";

  const handleSaveClick = () => {
    if (!form.name || !form.date) return;
    if (isRecurringEdit) { setEditScopeAsk(true); return; }
    onSave(form);
  };

  const applyEditScope = (scope) => {
    // scope: "occurrence" (this only) | "future" (all future)
    if (scope === "occurrence") {
      // Add the original date to the existing record's exceptions list and save
      // the form as a brand new one-off event on the same date.
      const original = event;
      const ymd = String(original.date).slice(0, 10);
      const exceptions = Array.isArray(original.recurrence?.exceptions) ? [...original.recurrence.exceptions] : [];
      if (!exceptions.includes(ymd)) exceptions.push(ymd);
      const updatedOriginal = { ...original, recurrence: { ...(original.recurrence || {}), exceptions } };
      onSave(updatedOriginal);
      // Save form as new occurrence (id stripped so backend creates it)
      const newOne = { ...form, id: undefined, recurrence: { type: "none", daysOfWeek: [], until: "" } };
      onSave(newOne);
    } else {
      // "future": just update the existing recurrence in place.
      onSave(form);
    }
    setEditScopeAsk(false);
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal cal-modal-scrollable" data-testid="event-form-modal" style={{maxWidth:480}}>
        <div className="modal-header"><div className="modal-title">{event ? (readonly ? "Event Details" : "Edit Event") : "New Event"}</div><button className="modal-close" onClick={onClose}>×</button></div>
        <div className="modal-body">
          <div className="form-group mb-14">
            <label>Cover Image</label>
            <CoverImage
              value={form.coverImage || ""}
              onChange={(v) => set("coverImage", v || "")}
              editable={isSuperAdmin && !readonly}
              title={form.name || "Event"}
              iconName="event-ticket"
              overlay={typeof form.overlay === "number" ? form.overlay : 55}
              textColor={form.textColor || "white"}
              textEffect={form.textEffect || "shadow"}
              data-testid="event-cover-image"
            />
            <div style={{ fontSize: 11, color: "var(--kc-sub)", marginTop: 6 }}>
              JPG, PNG, or WEBP up to 5 MB. Displays at 16:9 above the event card.
            </div>
            {isSuperAdmin && !readonly && (
              <TextStyleControls
                textColor={form.textColor || "white"}
                textEffect={form.textEffect || "shadow"}
                overlay={typeof form.overlay === "number" ? form.overlay : 55}
                onColor={(v) => set("textColor", v)}
                onEffect={(v) => set("textEffect", v)}
                onOverlay={(v) => set("overlay", v)}
                previewImage={form.coverImage || ""}
                previewTitle={form.name || "Event"}
                previewSubtitle={form.date || "Event date"}
              />
            )}
          </div>
          <div className="form-group mb-14"><label>Event Name *</label><input data-testid="event-name" disabled={readonly} value={form.name} onChange={e=>set("name",e.target.value)} placeholder="e.g. Easter Conference 2026"/></div>
          <div className="form-grid mb-14">
            <div className="form-group"><label>Date *</label><input data-testid="event-date" disabled={readonly} type="date" value={form.date} onChange={e=>set("date",e.target.value)}/></div>
            <div className="form-group"><label>Time</label><input data-testid="event-time" disabled={readonly} type="time" value={form.time || ""} onChange={e=>set("time",e.target.value)}/></div>
          </div>
          <div className="form-grid mb-14">
            <div className="form-group"><label>Capacity</label><input disabled={readonly} type="number" value={form.capacity} onChange={e=>set("capacity",e.target.value)} placeholder="e.g. 200"/></div>
            <ColorPickerField label="Color" testId="event-color" value={form.color} onChange={v => set("color", v)} />
          </div>
          <div className="form-group mb-14"><label>Description</label><textarea disabled={readonly} value={form.description} onChange={e=>set("description",e.target.value)}/></div>

          {/* Recurrence */}
          <div className="form-group mb-14">
            <label>Recurrence</label>
            <select data-testid="event-recurrence-type" disabled={readonly} value={form.recurrence?.type || "none"} onChange={e => setRec("type", e.target.value)}>
              <option value="none">Does not repeat</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly (select days)</option>
              <option value="biweekly">Bi-weekly</option>
              <option value="monthly-date">Monthly on the same date</option>
              <option value="monthly-weekday">Monthly on the same weekday</option>
              <option value="yearly">Yearly</option>
              <option value="custom">Custom interval</option>
            </select>
          </div>
          {form.recurrence?.type === "weekly" && (
            <div className="form-group mb-14" data-testid="event-recurrence-days">
              <label>Repeat on</label>
              <div className="flex gap-8 flex-wrap">
                {DOW_NAMES.map((n, i) => {
                  const selected = (form.recurrence?.daysOfWeek || []).includes(i);
                  return (
                    <button
                      key={n}
                      type="button"
                      data-testid={`event-recurrence-dow-${i}`}
                      disabled={readonly}
                      className={`cal-dow-chip ${selected ? "is-selected" : ""}`}
                      onClick={() => toggleDow(i)}
                    >{n}</button>
                  );
                })}
              </div>
            </div>
          )}
          {form.recurrence?.type === "custom" && (
            <div className="form-grid mb-14">
              <div className="form-group">
                <label>Every</label>
                <input data-testid="event-recurrence-interval" type="number" min={1} disabled={readonly} value={form.recurrence?.interval || 1} onChange={e => setRec("interval", parseInt(e.target.value) || 1)} />
              </div>
              <div className="form-group">
                <label>Unit</label>
                <select data-testid="event-recurrence-unit" disabled={readonly} value={form.recurrence?.unit || "weeks"} onChange={e => setRec("unit", e.target.value)}>
                  <option value="days">Day(s)</option>
                  <option value="weeks">Week(s)</option>
                  <option value="months">Month(s)</option>
                  <option value="years">Year(s)</option>
                </select>
              </div>
            </div>
          )}
          {form.recurrence?.type && form.recurrence.type !== "none" && (
            <div className="form-group mb-14">
              <label>End condition</label>
              <select data-testid="event-recurrence-end" disabled={readonly} value={form.recurrence?.end || "never"} onChange={e => setRec("end", e.target.value)}>
                <option value="never">No end date</option>
                <option value="count">End after N occurrences</option>
                <option value="until">End by date</option>
              </select>
            </div>
          )}
          {form.recurrence?.type && form.recurrence.type !== "none" && form.recurrence?.end === "count" && (
            <div className="form-group mb-14">
              <label>Number of occurrences</label>
              <input data-testid="event-recurrence-count" type="number" min={1} disabled={readonly} value={form.recurrence?.count || ""} onChange={e => setRec("count", e.target.value)} placeholder="e.g. 10" />
            </div>
          )}
          {form.recurrence?.type && form.recurrence.type !== "none" && form.recurrence?.end === "until" && (
            <div className="form-group mb-14">
              <label>End by date</label>
              <input data-testid="event-recurrence-until" disabled={readonly} type="date" value={form.recurrence?.until || ""} onChange={e => setRec("until", e.target.value)} />
            </div>
          )}

          <AuthorityAssignmentField
            value={form.authorityIds}
            onChange={v => set("authorityIds", v)}
            users={users}
            canEdit={!!canManageAuthority}
            testIdPrefix="event-authority"
          />
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>{readonly ? "Close" : "Cancel"}</button>
          {!readonly && <button className="btn btn-gold" data-testid="event-save" onClick={handleSaveClick}>Save Event</button>}
        </div>

        {editScopeAsk && (
          <div className="modal-overlay" data-testid="event-edit-scope-modal" onClick={() => setEditScopeAsk(false)}>
            <div className="modal-card cal-modal-scrollable" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
              <div className="cal-day-modal-header">
                <div className="cal-day-modal-title">Edit recurring event</div>
                <button type="button" className="cal-day-modal-close" data-testid="event-edit-scope-close" onClick={() => setEditScopeAsk(false)}>×</button>
              </div>
              <div className="cal-day-modal-body">
                <p style={{ fontSize: 13, color: "var(--kc-sub)", marginBottom: 12 }}>This event repeats. Apply your changes to:</p>
                <div className="flex" style={{ flexDirection: "column", gap: 8 }}>
                  <button type="button" className="btn btn-gold" data-testid="event-edit-scope-occurrence" onClick={() => applyEditScope("occurrence")}>This occurrence only</button>
                  <button type="button" className="btn btn-outline" data-testid="event-edit-scope-future" onClick={() => applyEditScope("future")}>All future occurrences</button>
                  <button type="button" className="btn btn-outline" onClick={() => setEditScopeAsk(false)}>Cancel</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


export { EventsPage };
